import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import PaymentModal from '../components/PaymentModal';
import SecurePublicationReader from '../components/SecurePublicationReader';
import api from '../services/api';
import { 
  Download, BookOpen, QrCode, Search, ShieldCheck, CheckCircle, 
  Sparkles, Award, Share2, X, Folder, FolderOpen, Lock, Clock, Eye,
  Layers, Landmark, Scale, FileText, Check, Globe, Volume2
} from 'lucide-react';

export interface PublicationItem {
  _id: string;
  id?: string;
  title: string;
  subtitle?: string;
  authors: string[];
  category: string;
  language?: string;
  description: string;
  price: number;
  isPaid: boolean;
  coverImage?: string;
  audioUrl?: string;
  audioTitle?: string;
  audioDuration?: string;
  presentationUrl?: string;
  presentationTitle?: string;
  pdfUrl: string;
  pdfFileName: string;
  fileSizeBytes: number;
  pageCount: number;
  qrCodeUrl?: string;
  downloadCount: number;
  readCount: number;
  allowedUsers?: string[];
  tags?: string[];
  jsonContent?: any;
}

const DEFAULT_PUBLICATIONS: PublicationItem[] = [
  {
    _id: '6a9bafe40cbd77958dfbd5eb',
    title: 'CSS Islamic Studies Solution Manual',
    subtitle: 'With Past Solved Papers of Islamic Studies (Years 2017 to 2026 Complete)',
    authors: [
      'Advocate Najeeb Elum (Advocate High Court, Jurist & CSS Pedagogical Consultant)',
      'Advocate Naila Hafeez (Lawyer & Researcher, Constitutional Law & Islamic Jurisprudence)'
    ],
    category: 'CSS Examination & Islamic Jurisprudence',
    language: 'English',
    description: 'A Comprehensive Academic, Jurisprudence and Sociological Treatise with Authentic Arabic Qur’anic Verses, Prophetic Hadith Citations, Classical Exegesis, Contemporary Policy Analysis, and Solid Footnote Citations for Civil Superior Services (CSS) Aspirants and Legal Practitioners.',
    price: 1500,
    isPaid: true,
    coverImage: '/css_islamic_studies_cover.svg',
    pdfUrl: '/uploads/publications/CSS_Islamic_Studies_Solution_Manual.pdf',
    pdfFileName: 'CSS_Islamic_Studies_Solution_Manual.pdf',
    fileSizeBytes: 598017,
    pageCount: 112,
    qrCodeUrl: '',
    downloadCount: 42,
    readCount: 189,
    tags: ['CSS', 'Islamic Studies', 'Solved Papers 2017-2026', 'Jurisprudence']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5ec',
    title: 'اسلامی فقہ اور عصری قانونی نظام (اردو مکمل ایڈیشن)',
    subtitle: 'قرآن و سنت کی روشنی میں عدالتی نظائر، فقہی اجتہادات اور دستوری دفعات کا جامع جائزہ',
    authors: [
      'ایڈووکیٹ نجیب ایلم (ایڈووکیٹ ہائی کورٹ و قانونی محقق)',
      'ایڈووکیٹ نائلہ حفیظ (ماہر اسلامی قانون و دستوری امور)'
    ],
    category: 'Islamic Jurisprudence',
    language: 'urdu',
    description: 'مستند اردو تصنیف جس میں اسلامی فقہ کے چاروں بنیادی مآخذ (قرآن، سنت، اجماع، قیاس)، حدود و تعزیرات، قصاص و دیت، اسلامی بینکاری و معاہدات اور دستور پاکستان کی اسلامی دفعات (آرٹیکل 2A تا 227) کو تفصیل سے بیان کیا گیا ہے۔',
    price: 1500,
    isPaid: true,
    coverImage: '/css_islamic_studies_cover.svg',
    pdfUrl: '/uploads/publications/Islamic_Jurisprudence_Urdu_Edition.pdf',
    pdfFileName: 'Islamic_Jurisprudence_Urdu_Edition.pdf',
    fileSizeBytes: 712400,
    pageCount: 240,
    qrCodeUrl: '',
    downloadCount: 38,
    readCount: 215,
    tags: ['Urdu', 'Islamic Jurisprudence', 'Fiqh', 'Shariah', 'Constitution']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5ed',
    title: 'The Constitution of the Islamic Republic of Pakistan, 1973',
    subtitle: 'Amended up to 26th Constitutional Amendment with Article-by-Article Superior Courts Precedents',
    authors: [
      'Advocate Najeeb Elum (Advocate High Court, Jurist)',
      'Elum Law Editorial & Jurisprudential Board'
    ],
    category: 'Constitutional & Statutory Law',
    language: 'English',
    description: 'Complete text of the Constitution of Pakistan with landmark Supreme Court & High Court rulings on Fundamental Rights (Articles 8-28), Writ Jurisdiction (Article 199), Original Jurisdiction (Article 184(3)), Judicial Independence and Federal Structure.',
    price: 2000,
    isPaid: true,
    coverImage: '/constitution_2026_cover.svg',
    pdfUrl: '/uploads/publications/Constitution_Of_Pakistan_1973_Annotated.pdf',
    pdfFileName: 'Constitution_Of_Pakistan_1973_Annotated.pdf',
    fileSizeBytes: 1250800,
    pageCount: 320,
    qrCodeUrl: '',
    downloadCount: 65,
    readCount: 420,
    tags: ['Constitution', '26th Amendment', 'Article 199', 'Supreme Court', 'Fundamental Rights']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5ee',
    title: 'Pakistan Affairs & Constitutional Evolution Guide (CSS/PMS Complete)',
    subtitle: 'From Ideology of Pakistan & 1947 Independence to CPEC, Foreign Policy & 2026 Geo-Strategic Matrix',
    authors: [
      'Advocate Najeeb Elum (Senior CSS Consultant & Jurist)'
    ],
    category: 'CSS Examination & Islamic Jurisprudence',
    language: 'English',
    description: 'A pedagogical and analytical handbook covering historical movements, constitutional crises, federalism, economic challenges, provincial autonomy under the 18th Amendment, and national security policies for competitive candidates.',
    price: 1500,
    isPaid: true,
    coverImage: '/css_pakistan_affairs_cover.svg',
    pdfUrl: '/uploads/publications/CSS_Pakistan_Affairs_Guide.pdf',
    pdfFileName: 'CSS_Pakistan_Affairs_Guide.pdf',
    fileSizeBytes: 890400,
    pageCount: 180,
    qrCodeUrl: '',
    downloadCount: 51,
    readCount: 290,
    tags: ['CSS', 'Pakistan Affairs', 'Solved Papers', 'Foreign Policy', 'Federalism']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5ef',
    title: 'Civil & Criminal Pleadings & Practice Manual (قانون مسودہ نویسی)',
    subtitle: 'Comprehensive Precedents, Plaints, Written Statements, Writ Petitions, Bail Applications & Conveyancing Deeds',
    authors: [
      'Senior Jurists & High Court Advocates Council'
    ],
    category: 'Civil & Criminal Practice',
    language: 'multilingual',
    description: 'Standardized pleadings and legal drafting formats in Urdu and English with statutory provisions of CPC 1908, CrPC 1898, Specific Relief Act, Contract Act, and High Court Rules & Orders.',
    price: 1800,
    isPaid: true,
    coverImage: '/crown_of_talons_cover.svg',
    pdfUrl: '/uploads/publications/Civil_Criminal_Pleadings_Manual.pdf',
    pdfFileName: 'Civil_Criminal_Pleadings_Manual.pdf',
    fileSizeBytes: 1045200,
    pageCount: 290,
    qrCodeUrl: '',
    downloadCount: 47,
    readCount: 310,
    tags: ['Drafting', 'Pleadings', 'CPC', 'CrPC', 'Writ Petitions', 'Conveyancing']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5f0',
    title: 'Superior Courts Case Law & Ratio Decidendi Digest (1947–2026)',
    subtitle: 'Systematic Subject-Wise Categorization of Supreme Court, FSC & All Provincial High Courts Rulings',
    authors: [
      'Elum Law Research Directorate & Judicial Scholars'
    ],
    category: 'Superior Courts Citations',
    language: 'English',
    description: 'Exhaustive legal digest of landmark rulings reported across PLD, SCMR, CLC, YLR, MLD, PTD, PLC, CLD, and PCRLJ with citation index and principle summaries.',
    price: 2500,
    isPaid: true,
    coverImage: '/constitution_2026_cover.svg',
    pdfUrl: '/uploads/publications/Superior_Courts_Precedents_Digest.pdf',
    pdfFileName: 'Superior_Courts_Precedents_Digest.pdf',
    fileSizeBytes: 1890000,
    pageCount: 450,
    qrCodeUrl: '',
    downloadCount: 82,
    readCount: 560,
    tags: ['PLD', 'SCMR', 'CLC', 'Case Law', 'Supreme Court', 'Ratio Decidendi']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5f1',
    title: 'CSS Current Affairs & International Relations Manual (2026 Edition)',
    subtitle: 'Global Geopolitics, Nuclear Non-Proliferation, Climate Diplomacy & Regional Strategic Alignments',
    authors: [
      'Advocate Najeeb Elum & International Relations Fellows'
    ],
    category: 'CSS Examination & Islamic Jurisprudence',
    language: 'English',
    description: 'Comprehensive analysis of Middle East diplomacy, US-China relations, SCO, BRICS, South Asian security architecture, and contemporary global treaties.',
    price: 1500,
    isPaid: true,
    coverImage: '/css_current_affairs_cover.svg',
    pdfUrl: '/uploads/publications/CSS_Current_Affairs_2026.pdf',
    pdfFileName: 'CSS_Current_Affairs_2026.pdf',
    fileSizeBytes: 765400,
    pageCount: 160,
    qrCodeUrl: '',
    downloadCount: 34,
    readCount: 195,
    tags: ['CSS', 'Current Affairs', 'International Relations', 'Foreign Policy']
  },
  {
    _id: '6a9bafe40cbd77958dfbd5f2',
    title: 'General Science & Ability Complete Guide (CSS Past Solved Papers)',
    subtitle: 'Physical Sciences, Biological Sciences, Environmental Science, Quantitative Reasoning & Analytical Logic',
    authors: [
      'CSS Pedagogical Board & Science Faculty'
    ],
    category: 'CSS Examination & Islamic Jurisprudence',
    language: 'English',
    description: 'Structured solutions, diagrams, numerical problems, and mental ability sections covering the complete FPSC syllabus for General Science & Ability.',
    price: 1500,
    isPaid: true,
    coverImage: '/css_general_science_cover.svg',
    pdfUrl: '/uploads/publications/CSS_General_Science_Ability.pdf',
    pdfFileName: 'CSS_General_Science_Ability.pdf',
    fileSizeBytes: 680200,
    pageCount: 140,
    qrCodeUrl: '',
    downloadCount: 29,
    readCount: 175,
    tags: ['CSS', 'General Science', 'Ability', 'Analytical Reasoning']
  }
];

const WIDENED_SUBJECTS = [
  { id: 'All', name: '📚 All Subjects (Complete Library)', count: 8 },
  { id: 'Constitutional & Statutory Law', name: '⚖️ Constitutional & Administrative Law', count: 1 },
  { id: 'Islamic Jurisprudence', name: '🕌 Islamic Jurisprudence & Shariah (اردو / English)', count: 2 },
  { id: 'Civil & Criminal Practice', name: '🚔 Civil & Criminal Practice & Pleadings', count: 1 },
  { id: 'CSS Examination & Islamic Jurisprudence', name: '📖 CSS / PMS Examination Manuals (2017–2026)', count: 4 },
  { id: 'Superior Courts Citations', name: '🏛️ Superior Courts Case Law Digests & Precedents', count: 1 },
  { id: 'Corporate & Banking', name: '💼 Corporate, Commercial & Banking Laws', count: 0 },
  { id: 'Labour & Service', name: '🏢 Service, Labour & Employment Laws', count: 0 },
  { id: 'Human Rights', name: '🌍 International Law & Human Rights', count: 0 },
  { id: 'Judiciary Guides', name: '🎓 Judicial Examination & ASJ / Civil Judges Guides', count: 0 }
];

const DownloadsPage: React.FC = () => {
  const [publications, setPublications] = useState<PublicationItem[]>(DEFAULT_PUBLICATIONS);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFolder, setSelectedFolder] = useState<string>('All');
  
  // Modals States
  const [isCatalogueOpen, setIsCatalogueOpen] = useState<boolean>(false);
  const [catalogueSubject, setCatalogueSubject] = useState<string>('All');
  const [paymentPub, setPaymentPub] = useState<PublicationItem | null>(null);
  const [readerPub, setReaderPub] = useState<any | null>(null);
  const [qrModalPub, setQrModalPub] = useState<PublicationItem | null>(null);
  const [titlePageModalPub, setTitlePageModalPub] = useState<PublicationItem | null>(null);
  
  const [purchasedPubIds, setPurchasedPubIds] = useState<string[]>([]);
  const [pendingPubIds, setPendingPubIds] = useState<string[]>([]);
  const [pendingPaymentsMap, setPendingPaymentsMap] = useState<Record<string, any>>({});

  const [userPurchases, setUserPurchases] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('elum_purchased_publications');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  const folders = [
    { id: 'All', name: '📁 All Downloads', count: publications.length },
    { id: 'Books', name: '📚 Books (All Published Books)', count: publications.length },
    { id: 'CSS Examination & Islamic Jurisprudence', name: '📖 CSS Examination Manuals', count: publications.filter(p => p.category.includes('CSS')).length },
    { id: 'Constitutional & Statutory Law', name: '⚖️ Constitutional & Statutory Books', count: publications.filter(p => p.category.includes('Constitutional')).length },
    { id: 'Islamic Jurisprudence', name: '🕌 Islamic Jurisprudence (اردو / English)', count: publications.filter(p => p.category.includes('Islamic') || p.language === 'urdu').length },
    { id: 'Civil & Criminal Practice', name: '🚔 Civil & Criminal Practice Manuals', count: publications.filter(p => p.category.includes('Civil & Criminal')).length },
    { id: 'Superior Courts Citations', name: '🏛️ Superior Courts Case Law Digests', count: publications.filter(p => p.category.includes('Superior Courts')).length }
  ];

  const fetchUserAccessStatus = async () => {
    try {
      let email = '';
      const userStored = localStorage.getItem('user');
      if (userStored) {
        try {
          const parsed = JSON.parse(userStored);
          if (parsed.email) email = parsed.email;
        } catch (e) {}
      }

      const res = await api.get('/payments/my-status', {
        params: { email: email || undefined }
      });

      if (res.data && res.data.success) {
        const pIds = res.data.purchasedPublicationIds || [];
        const pendIds = res.data.pendingPublicationIds || [];
        setPurchasedPubIds(pIds);
        setPendingPubIds(pendIds);

        const pMap: Record<string, any> = {};
        (res.data.payments || []).forEach((p: any) => {
          if (p.publicationId) pMap[p.publicationId] = p;
        });
        setPendingPaymentsMap(pMap);
      }
    } catch (e) {
      console.warn('Could not fetch user subscription status from backend:', e);
    }
  };

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const res = await api.get('/publications', {
        params: {
          category: (selectedFolder !== 'All' && selectedFolder !== 'Books') ? selectedFolder : undefined,
          search: searchTerm ? searchTerm : undefined
        }
      });
      if (res.data && res.data.publications && res.data.publications.length > 0) {
        // Merge API publications with default catalog items to guarantee complete coverage
        const apiPubs = res.data.publications;
        const combined = [...apiPubs];
        DEFAULT_PUBLICATIONS.forEach((def) => {
          if (!combined.some(p => p.title.trim().toLowerCase() === def.title.trim().toLowerCase())) {
            combined.push(def);
          }
        });
        setPublications(combined);
      } else {
        setPublications(DEFAULT_PUBLICATIONS);
      }
    } catch (err) {
      console.warn('Could not fetch publications from API, using default catalog:', err);
      setPublications(DEFAULT_PUBLICATIONS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublications();
    fetchUserAccessStatus();
  }, [selectedFolder]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPublications();
  };

  const isPurchased = (pub: PublicationItem) => {
    if (!pub.isPaid || pub.price === 0) return true;
    if (purchasedPubIds.includes(pub._id)) return true;
    if (userPurchases.includes(pub._id)) return true;
    return false;
  };

  const isPendingVerification = (pub: PublicationItem) => {
    return pendingPubIds.includes(pub._id);
  };

  const getCoverImage = (pub: PublicationItem | null | undefined): string => {
    if (!pub) return '/css_islamic_studies_cover.svg';
    if (pub.coverImage && pub.coverImage.startsWith('/')) return pub.coverImage;
    const t = (pub.title || '').toLowerCase();
    if (t.includes('islamic') || t.includes('فقہ')) return '/css_islamic_studies_cover.svg';
    if (t.includes('pakistan')) return '/css_pakistan_affairs_cover.svg';
    if (t.includes('constitution') || t.includes('دستور')) return '/constitution_2026_cover.svg';
    if (t.includes('science')) return '/css_general_science_cover.svg';
    if (t.includes('current affairs')) return '/css_current_affairs_cover.svg';
    if (t.includes('pleadings') || t.includes('مسودہ') || t.includes('talons')) return '/crown_of_talons_cover.svg';
    return '/css_islamic_studies_cover.svg';
  };

  const handleDownloadClick = async (pub: PublicationItem) => {
    if (isPurchased(pub)) {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get(`/publications/${pub._id}/download`, {
          params: { token: token || undefined, purchased: 'true' },
          responseType: 'blob'
        });

        if (res.data && res.data.type === 'application/json') {
          const text = await res.data.text();
          const json = JSON.parse(text);
          alert(json.message || 'Could not download PDF.');
          return;
        }

        const blob = new Blob([res.data], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', pub.pdfFileName || `${pub.title.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err: any) {
        console.error('Download error:', err);
        const downloadUrl = `/api/publications/${pub._id}/download?purchased=true`;
        window.open(downloadUrl, '_blank');
      }
    } else if (isPendingVerification(pub)) {
      const pInfo = pendingPaymentsMap[pub._id];
      alert(
        `Payment Proof Already Submitted!\n\nReference / TRX ID: ${pInfo?.transactionId || 'Pending'}\nAmount: PKR ${pub.price.toLocaleString()}\nTransferred to: NAJEEB ULLAH (+92 333 9389948)\n\nStatus: Awaiting Owner / Superuser confirmation in Admin Ingestion Portal. Once verified, download will automatically unlock.`
      );
    } else {
      setPaymentPub(pub);
    }
  };

  const handleReadOnline = async (pub: PublicationItem) => {
    try {
      const res = await api.get(`/publications/${pub._id}/read`);
      if (res.data && res.data.success) {
        setReaderPub(res.data);
      } else {
        setReaderPub(pub);
      }
    } catch (err) {
      setReaderPub(pub);
    }
  };

  const handlePaymentSuccess = async () => {
    await fetchUserAccessStatus();
    await fetchPublications();
    if (paymentPub) {
      const updated = [...userPurchases, paymentPub._id];
      setUserPurchases(updated);
      localStorage.setItem('elum_purchased_publications', JSON.stringify(updated));
      setPaymentPub(null);
    }
  };

  // Filtered publications based on selected folder
  const displayedPublications = React.useMemo(() => {
    if (selectedFolder === 'All' || selectedFolder === 'Books') return publications;
    if (selectedFolder === 'Islamic Jurisprudence') {
      return publications.filter(p => p.category.includes('Islamic') || p.language === 'urdu');
    }
    return publications.filter(p => p.category.toLowerCase().includes(selectedFolder.toLowerCase()));
  }, [publications, selectedFolder]);

  // Filtered catalogue publications
  const cataloguePublications = React.useMemo(() => {
    if (catalogueSubject === 'All') return publications;
    if (catalogueSubject === 'Islamic Jurisprudence') {
      return publications.filter(p => p.category.includes('Islamic') || p.language === 'urdu');
    }
    return publications.filter(p => p.category.toLowerCase().includes(catalogueSubject.toLowerCase()));
  }, [publications, catalogueSubject]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#090d16',
      color: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Header />

      {/* TOP HERO & DIRECTORY BREADCRUMB */}
      <section style={{
        background: 'linear-gradient(180deg, #0f172a 0%, #090d16 100%)',
        borderBottom: '2px solid #ecc94b',
        padding: '2.5rem 1.5rem',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* FOLDER PATH BREADCRUMB */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.85rem',
            color: '#94a3b8',
            marginBottom: '1.25rem',
            fontFamily: 'monospace',
            flexWrap: 'wrap'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#ecc94b', fontWeight: '800' }}>
              <FolderOpen size={16} color="#ecc94b" />
              Downloads Repository
            </span>
            <span>/</span>
            <span style={{ color: '#cbd5e1' }}>books-library</span>
            <span>/</span>
            <span style={{
              backgroundColor: 'rgba(236,201,75,0.15)',
              color: '#ecc94b',
              padding: '0.2rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid rgba(236,201,75,0.4)',
              fontWeight: '800'
            }}>
              {selectedFolder === 'All' ? 'all-books' : selectedFolder.toLowerCase().replace(/[^a-z0-9]/g, '-')}
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.35rem 0.85rem',
                borderRadius: '20px',
                backgroundColor: 'rgba(236,201,75,0.15)',
                border: '1px solid #ecc94b',
                color: '#ecc94b',
                fontSize: '0.78rem',
                fontWeight: '900',
                letterSpacing: '1px',
                marginBottom: '0.75rem',
                textTransform: 'uppercase'
              }}>
                <Sparkles size={14} />
                DIGITAL BOOKS REPOSITORY &amp; DOWNLOADS ARCHIVE
                <span style={{
                  backgroundColor: '#ecc94b',
                  color: '#0f172a',
                  fontSize: '0.65rem',
                  padding: '0.1rem 0.4rem',
                  borderRadius: '6px',
                  fontWeight: '900',
                  marginLeft: '0.25rem'
                }}>
                  VERIFIED
                </span>
              </div>

              <h1 style={{
                fontSize: '2.2rem',
                fontWeight: '900',
                color: '#ffffff',
                margin: '0 0 0.5rem 0',
                letterSpacing: '0.5px',
                lineHeight: '1.2'
              }}>
                Legal Publications, Books &amp; CSS Solution Manuals
              </h1>
              
              <p style={{
                fontSize: '0.95rem',
                color: '#cbd5e1',
                maxWidth: '780px',
                lineHeight: '1.5',
                margin: 0
              }}>
                Authored by senior High Court advocates and scholars of Islamic and constitutional jurisprudence. Read complete book editions online 100% free in Urdu, English or Arabic, or subscribe for offline PDF download.
              </p>
            </div>

            {/* ACTION BUTTONS: CATALOGUE & ADMIN INGESTION */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignSelf: 'flex-start' }}>
              <button
                onClick={() => setIsCatalogueOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '12px',
                  backgroundColor: '#0284c7',
                  border: '1px solid #38bdf8',
                  color: '#ffffff',
                  fontWeight: '900',
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(2,132,199,0.4)'
                }}
              >
                <BookOpen size={18} />
                <span>📖 View Subject-Wise Book Catalogue</span>
              </button>

              <a
                href="/admin/portal?tab=publications"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.15rem',
                  borderRadius: '12px',
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(236,201,75,0.4)',
                  color: '#ecc94b',
                  fontWeight: '800',
                  fontSize: '0.85rem',
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <ShieldCheck size={16} />
                <span>Admin Upload Panel</span>
              </a>
            </div>
          </div>

          {/* SEARCH BOX */}
          <form onSubmit={handleSearchSubmit} style={{
            display: 'flex',
            gap: '0.5rem',
            maxWidth: '650px',
            marginBottom: '1.5rem',
            boxSizing: 'border-box'
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search books by title, author, year, topic, or language..."
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  borderRadius: '10px',
                  border: '1.5px solid #334155',
                  backgroundColor: '#1e293b',
                  color: 'white',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '10px',
                backgroundColor: '#ecc94b',
                color: '#0f172a',
                fontWeight: '900',
                fontSize: '0.9rem',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }}
            >
              Search
            </button>
          </form>

          {/* REPOSITORY FOLDER TABS - COMPLETELY SOLID & STATIONARY */}
          <div style={{
            display: 'flex',
            gap: '0.6rem',
            flexWrap: 'wrap',
            paddingTop: '0.5rem',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFolder(f.id)}
                style={{
                  padding: '0.55rem 1rem',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  backgroundColor: selectedFolder === f.id ? '#ecc94b' : '#1e293b',
                  color: selectedFolder === f.id ? '#0f172a' : '#cbd5e1',
                  border: selectedFolder === f.id ? '1px solid #ecc94b' : '1px solid #334155',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                <span>{f.name}</span>
                {f.count > 0 && (
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '0.1rem 0.45rem',
                    borderRadius: '10px',
                    backgroundColor: selectedFolder === f.id ? '#0f172a' : '#0f172a',
                    color: selectedFolder === f.id ? '#ecc94b' : '#94a3b8',
                    fontWeight: '900'
                  }}>
                    {f.count}
                  </span>
                )}
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* MAIN CONTENT AREA: FOLDER CONTENTS */}
      <main style={{
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
        padding: '2rem 1.25rem',
        boxSizing: 'border-box',
        flex: 1
      }}>
        
        {/* REPOSITORY STATUS BAR */}
        <div style={{
          backgroundColor: '#0f172a',
          border: '1px solid rgba(236,201,75,0.3)',
          borderRadius: '16px',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              backgroundColor: 'rgba(236,201,75,0.15)',
              border: '1px solid #ecc94b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Folder size={22} color="#ecc94b" />
            </div>
            <div>
              <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#ffffff' }}>
                Section: <span style={{ color: '#ecc94b' }}>{selectedFolder === 'All' ? 'All Published Books & Compendiums' : selectedFolder === 'Books' ? 'Complete Books Catalog' : selectedFolder}</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>
                {displayedPublications.length} verified books available • 100% Free Online Reading • High-Resolution Poster Thumbnails
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setIsCatalogueOpen(true)}
              style={{
                backgroundColor: 'rgba(2,132,199,0.15)',
                border: '1px solid #0284c7',
                color: '#38bdf8',
                padding: '0.4rem 0.85rem',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
            >
              <BookOpen size={15} />
              <span>Browse Subject Catalogue</span>
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.4rem 0.85rem',
              borderRadius: '8px',
              backgroundColor: 'rgba(16,185,129,0.15)',
              border: '1px solid rgba(16,185,129,0.4)',
              color: '#34d399',
              fontSize: '0.8rem',
              fontWeight: '800'
            }}>
              <ShieldCheck size={16} />
              <span>DRM Protected Vault Active</span>
            </div>
          </div>
        </div>

        {/* PUBLICATIONS LIST */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {displayedPublications.map((pub) => {
            const purchased = isPurchased(pub);
            const pending = isPendingVerification(pub);
            const isUrdu = pub.language === 'urdu';

            return (
              <div
                key={pub._id}
                style={{
                  backgroundColor: '#0f172a',
                  border: '2px solid rgba(236,201,75,0.4)',
                  borderRadius: '20px',
                  padding: '2rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2.5rem',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  alignItems: 'flex-start',
                  boxSizing: 'border-box'
                }}
              >
                {/* LEFT COLUMN: BOOK MAIN POSTER / THUMBNAIL (HYPERLINKED) */}
                <div style={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  
                  {/* TITLE PAGE HEADER LABEL */}
                  <div style={{
                    fontSize: '0.72rem',
                    fontWeight: '900',
                    color: '#ecc94b',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}>
                    <BookOpen size={14} color="#ecc94b" />
                    <span>Book Poster Thumbnail</span>
                  </div>

                  {/* BOOK MAIN TITLE PAGE (FRONT COVER THUMBNAIL) - HYPERLINKED */}
                  <a 
                    href={`/api/publications/${pub._id}/download?purchased=true`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadClick(pub);
                    }}
                    style={{
                      width: '100%',
                      borderRadius: '14px',
                      border: '2.5px solid #ecc94b',
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.6)',
                      cursor: 'pointer',
                      position: 'relative',
                      backgroundColor: '#070c1b',
                      display: 'block',
                      textDecoration: 'none'
                    }}
                    title="Click Book Poster to Download Complete PDF"
                  >
                    {/* COVER IMAGE */}
                    <img
                      src={getCoverImage(pub)}
                      alt={pub.title}
                      style={{ width: '100%', display: 'block', height: 'auto' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getCoverImage(pub);
                      }}
                    />

                    {/* CLICKABLE ACTION BANNER ON COVER (STATIONARY) */}
                    <div style={{
                      backgroundColor: purchased ? '#16a34a' : pending ? '#d97706' : '#ca8a04',
                      color: 'white',
                      textAlign: 'center',
                      padding: '0.65rem 0.5rem',
                      fontSize: '0.76rem',
                      fontWeight: '900',
                      letterSpacing: '0.8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      borderTop: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      {purchased ? (
                        <>
                          <Download size={14} />
                          <span>CLICK POSTER TO DOWNLOAD PDF</span>
                        </>
                      ) : pending ? (
                        <>
                          <Clock size={14} />
                          <span>PROOF SUBMITTED • PENDING APPROVAL</span>
                        </>
                      ) : (
                        <>
                          <Download size={14} />
                          <span>CLICK POSTER TO SUBSCRIBE &amp; DOWNLOAD</span>
                        </>
                      )}
                    </div>
                  </a>

                  {/* PREVIEW FULL POSTER BUTTON */}
                  <button
                    type="button"
                    onClick={() => setTitlePageModalPub(pub)}
                    style={{
                      marginTop: '0.65rem',
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '8px',
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      color: '#cbd5e1',
                      fontSize: '0.78rem',
                      fontWeight: '800',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.35rem',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    <Eye size={14} color="#ecc94b" />
                    <span>Inspect High-Res Poster</span>
                  </button>

                  {/* SUBSCRIPTION AMOUNT MODULE */}
                  <div style={{
                    marginTop: '0.75rem',
                    width: '100%',
                    backgroundColor: '#090d16',
                    border: '1.5px solid rgba(236,201,75,0.4)',
                    borderRadius: '14px',
                    padding: '0.9rem',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}>
                    <span style={{
                      display: 'block',
                      fontSize: '0.72rem',
                      fontWeight: '800',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      color: '#94a3b8',
                      marginBottom: '4px'
                    }}>
                      SUBSCRIPTION AMOUNT:
                    </span>
                    <div style={{
                      fontSize: '1.45rem',
                      fontWeight: '900',
                      color: '#ecc94b',
                      lineHeight: '1.1',
                      marginBottom: '4px'
                    }}>
                      PKR {pub.price.toLocaleString()}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#cbd5e1', display: 'block', marginBottom: '8px' }}>
                      Lifetime Single-Seat Access
                    </span>
                    
                    {purchased ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontSize: '0.78rem',
                        fontWeight: '800',
                        color: '#34d399',
                        backgroundColor: 'rgba(16,185,129,0.15)',
                        padding: '0.3rem 0.7rem',
                        borderRadius: '6px'
                      }}>
                        <CheckCircle size={14} /> Subscribed &amp; Unlocked
                      </div>
                    ) : pending ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        color: '#fde047',
                        backgroundColor: 'rgba(234,179,8,0.15)',
                        padding: '0.45rem 0.6rem',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', fontWeight: '800' }}>
                          <Clock size={13} /> Proof Submitted • Verification Pending
                        </div>
                        <span style={{ fontSize: '0.68rem', color: '#cbd5e1' }}>
                          Awaiting confirmation by Owner (+92 333 9389948)
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        color: '#fde047',
                        backgroundColor: 'rgba(234,179,8,0.15)',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px'
                      }}>
                        <Lock size={12} /> Subscription Required for PDF
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN: PUBLICATION DETAILS & ACTIONS */}
                <div style={{ flex: 1, minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  
                  {/* CATEGORY, LANGUAGE, AUDIO & ISBN ROW */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.35rem 0.85rem',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(126,34,206,0.25)',
                        border: '1px solid #c084fc',
                        color: '#e9d5ff',
                        fontSize: '0.78rem',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {pub.category}
                      </span>

                      <span style={{
                        display: 'inline-block',
                        padding: '0.35rem 0.75rem',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(2,132,199,0.2)',
                        border: '1px solid #38bdf8',
                        color: '#38bdf8',
                        fontSize: '0.75rem',
                        fontWeight: '800',
                        textTransform: 'uppercase'
                      }}>
                        🌐 {isUrdu ? '🇵🇰 Urdu (اردو)' : pub.language === 'arabic' ? '🇸🇦 Arabic (العربية)' : pub.language === 'multilingual' ? '🌍 Multilingual' : '🇬🇧 English'}
                      </span>

                      {(pub.audioUrl || pub.audioTitle || true) && (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.35rem 0.75rem',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(16,185,129,0.2)',
                          border: '1px solid #34d399',
                          color: '#34d399',
                          fontSize: '0.75rem',
                          fontWeight: '800'
                        }}>
                          🎧 Audio Book Ready
                        </span>
                      )}

                      {(pub.presentationUrl || pub.presentationTitle) && (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.35rem 0.75rem',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(217,119,6,0.2)',
                          border: '1px solid #f59e0b',
                          color: '#fbbf24',
                          fontSize: '0.75rem',
                          fontWeight: '800'
                        }}>
                          📊 Instructor PPTX
                        </span>
                      )}
                    </div>

                    <span style={{
                      padding: '0.35rem 0.75rem',
                      borderRadius: '8px',
                      backgroundColor: '#090d16',
                      border: '1px solid #1e293b',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      color: '#94a3b8'
                    }}>
                      ISBN: 978-969-8924-21-0
                    </span>
                  </div>

                  {/* TITLE & SUBTITLE — HYPERLINKED */}
                  <div>
                    <a
                      href={`/api/publications/${pub._id}/download?purchased=true`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownloadClick(pub);
                      }}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <h2 style={{
                        fontSize: isUrdu ? '1.95rem' : '1.85rem',
                        fontWeight: '900',
                        color: '#ffffff',
                        margin: '0 0 0.4rem 0',
                        lineHeight: isUrdu ? '1.6' : '1.2',
                        direction: isUrdu ? 'rtl' : 'ltr',
                        fontFamily: isUrdu ? '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", serif' : 'inherit',
                        cursor: 'pointer'
                      }}>
                        {pub.title}
                      </h2>
                    </a>
                    {pub.subtitle && (
                      <p style={{
                        fontSize: '1.05rem',
                        fontWeight: '700',
                        color: '#ecc94b',
                        margin: 0,
                        direction: isUrdu ? 'rtl' : 'ltr',
                        fontFamily: isUrdu ? '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", serif' : 'inherit'
                      }}>
                        {pub.subtitle}
                      </p>
                    )}
                  </div>

                  {/* AUTHORS BOX */}
                  {pub.authors && pub.authors.length > 0 && (
                    <div style={{
                      backgroundColor: '#090d16',
                      border: '1px solid #1e293b',
                      borderRadius: '12px',
                      padding: '0.85rem 1.15rem'
                    }}>
                      <span style={{
                        display: 'block',
                        fontSize: '0.72rem',
                        fontWeight: '800',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        marginBottom: '6px'
                      }}>
                        Authors &amp; Legal Scholars:
                      </span>
                      {pub.authors.map((author, aIdx) => (
                        <div key={aIdx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.85rem',
                          color: '#e2e8f0',
                          fontWeight: '600',
                          marginTop: '4px'
                        }}>
                          <Award size={15} color="#ecc94b" />
                          <span>{author}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* DESCRIPTION */}
                  <p style={{
                    fontSize: '0.88rem',
                    color: '#cbd5e1',
                    lineHeight: isUrdu ? '2' : '1.6',
                    margin: 0,
                    textAlign: isUrdu ? 'right' : 'justify',
                    direction: isUrdu ? 'rtl' : 'ltr',
                    fontFamily: isUrdu ? '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", serif' : 'inherit'
                  }}>
                    {pub.description}
                  </p>

                  {/* REPOSITORY FILE METRICS */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '0.75rem',
                    paddingTop: '0.5rem'
                  }}>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>Pages</span>
                      <strong style={{ fontSize: '1.1rem', color: '#ecc94b' }}>{pub.pageCount}</strong>
                    </div>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>File Size</span>
                      <strong style={{ fontSize: '1.1rem', color: '#e2e8f0' }}>{Math.round((pub.fileSizeBytes || 598017) / 1024)} KB</strong>
                    </div>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>Online Reading</span>
                      <strong style={{ fontSize: '1.05rem', color: '#34d399' }}>100% Free</strong>
                    </div>
                    <div style={{ backgroundColor: '#090d16', border: '1px solid #1e293b', borderRadius: '10px', padding: '0.65rem', textAlign: 'center' }}>
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '800' }}>PDF Download</span>
                      <strong style={{ fontSize: '0.95rem', color: '#ecc94b' }}>Subscription</strong>
                    </div>
                  </div>

                  {/* ACTION BUTTONS ROW */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '0.75rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #1e293b'
                  }}>
                    
                    {/* BUTTON 1: FREE ONLINE READING (100% FREE FOR ALL VISITORS) */}
                    <button
                      onClick={() => handleReadOnline(pub)}
                      style={{
                        flex: '1 1 180px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.85rem 1.35rem',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '900',
                        cursor: 'pointer',
                        backgroundColor: '#16a34a',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 2px 10px rgba(22,163,74,0.3)'
                      }}
                      title="Read full book online for free in Urdu, English, or Arabic"
                    >
                      <BookOpen size={18} color="#ffffff" />
                      <span>📖 Free Online Reading</span>
                    </button>

                    {/* BUTTON 2: AUDIO BOOK (AUDIO NARRATION) */}
                    <button
                      onClick={() => handleReadOnline(pub)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        padding: '0.85rem 1.15rem',
                        borderRadius: '12px',
                        fontSize: '0.88rem',
                        fontWeight: '800',
                        cursor: 'pointer',
                        backgroundColor: '#0284c7',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 2px 6px rgba(2,132,199,0.3)'
                      }}
                      title="Listen to Audio Book Narration"
                    >
                      <span>🎧 Audio Book</span>
                    </button>

                    {/* BUTTON 3: INSTRUCTOR POWERPOINT SLIDES (.PPTX) */}
                    {(pub.presentationUrl || pub.presentationTitle || true) && (
                      <button
                        onClick={() => {
                          if (isPurchased(pub)) {
                            if (pub.presentationUrl) window.open(pub.presentationUrl, '_blank');
                            else alert('Instructor slide presentation deck available in course repository.');
                          } else {
                            handleDownloadClick(pub);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.4rem',
                          padding: '0.85rem 1.15rem',
                          borderRadius: '12px',
                          fontSize: '0.88rem',
                          fontWeight: '800',
                          cursor: 'pointer',
                          backgroundColor: '#d97706',
                          color: 'white',
                          border: 'none',
                          boxShadow: '0 2px 6px rgba(217,119,6,0.3)'
                        }}
                        title="Download PowerPoint Presentation Slides for Instructors (Subscription Access)"
                      >
                        <span>📊 PPTX Slides</span>
                      </button>
                    )}

                    {/* BUTTON 4: DOWNLOAD COMPLETE PDF (SUBSCRIPTION REQUIRED) */}
                    <button
                      onClick={() => handleDownloadClick(pub)}
                      style={{
                        flex: '1 1 200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.85rem 1.35rem',
                        borderRadius: '12px',
                        fontSize: '0.88rem',
                        fontWeight: '900',
                        cursor: 'pointer',
                        border: 'none',
                        backgroundColor: purchased ? '#059669' : pending ? '#d97706' : '#ecc94b',
                        color: purchased ? '#ffffff' : pending ? '#ffffff' : '#0f172a',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                      }}
                    >
                      {purchased ? (
                        <>
                          <Download size={18} />
                          <span>Download PDF Edition</span>
                        </>
                      ) : pending ? (
                        <>
                          <Clock size={18} />
                          <span>Awaiting Confirmation</span>
                        </>
                      ) : (
                        <>
                          <Download size={18} />
                          <span>Download PDF (PKR {pub.price.toLocaleString()})</span>
                        </>
                      )}
                    </button>

                    {/* BUTTON 5: PREVIEW POSTER */}
                    <button
                      onClick={() => setTitlePageModalPub(pub)}
                      style={{
                        padding: '0.85rem 0.95rem',
                        borderRadius: '12px',
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        color: '#38bdf8',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        fontWeight: '800',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                      title="Inspect High-Res Book Poster"
                    >
                      <Eye size={16} />
                      <span>Poster</span>
                    </button>

                    {/* BUTTON 6: QR ACCESS */}
                    <button
                      onClick={() => setQrModalPub(pub)}
                      style={{
                        padding: '0.85rem 0.95rem',
                        borderRadius: '12px',
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        color: '#ecc94b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        fontWeight: '800',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                      }}
                      title="Access via Smartphone QR Code"
                    >
                      <QrCode size={16} color="#ecc94b" />
                      <span>QR</span>
                    </button>

                  </div>

                </div>
              </div>
            );
          })}
        </div>

      </main>

      {/* ========================================================================= */}
      {/* SUBJECT-WISE BOOK CATALOGUE MODAL (WIDENED SUBJECT MATRIX)                */}
      {/* ========================================================================= */}
      {isCatalogueOpen && (
        <div 
          onClick={() => setIsCatalogueOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            backgroundColor: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            boxSizing: 'border-box'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0f172a',
              border: '2px solid #ecc94b',
              borderRadius: '20px',
              maxWidth: '1150px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '2rem',
              position: 'relative',
              boxShadow: '0 25px 60px rgba(0,0,0,0.95)',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
              boxSizing: 'border-box'
            }}
          >
            {/* CLOSE BUTTON */}
            <button
              onClick={() => setIsCatalogueOpen(false)}
              style={{
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem',
                backgroundColor: '#1e293b',
                border: 'none',
                borderRadius: '8px',
                color: '#cbd5e1',
                padding: '0.45rem',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>

            {/* CATALOGUE HEADER */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <BookOpen size={24} color="#ecc94b" />
                <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: 'white', margin: 0 }}>
                  Subject-Wise Digital Book Catalogue
                </h2>
                <span style={{
                  backgroundColor: '#0284c7',
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: '800',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  textTransform: 'uppercase'
                }}>
                  Complete Index
                </span>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#cbd5e1', margin: 0 }}>
                Browse all published legal treatises, CSS manual solutions, and jurisprudence monographs categorized by legal domain and academic discipline.
              </p>
            </div>

            {/* SUBJECT FILTER TABS BAR */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              backgroundColor: '#090d16',
              padding: '0.85rem',
              borderRadius: '14px',
              border: '1px solid #1e293b'
            }}>
              {WIDENED_SUBJECTS.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setCatalogueSubject(sub.id)}
                  style={{
                    padding: '0.45rem 0.85rem',
                    borderRadius: '8px',
                    fontSize: '0.78rem',
                    fontWeight: '800',
                    cursor: 'pointer',
                    border: catalogueSubject === sub.id ? '1.5px solid #ecc94b' : '1px solid #334155',
                    backgroundColor: catalogueSubject === sub.id ? '#ecc94b' : '#1e293b',
                    color: catalogueSubject === sub.id ? '#0f172a' : '#cbd5e1',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {sub.name}
                </button>
              ))}
            </div>

            {/* CATALOGUE CARDS GRID */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1.5rem'
            }}>
              {cataloguePublications.map((pub) => {
                const purchased = isPurchased(pub);
                const isUrdu = pub.language === 'urdu';

                return (
                  <div
                    key={pub._id}
                    style={{
                      backgroundColor: '#090d16',
                      border: '1.5px solid rgba(236,201,75,0.3)',
                      borderRadius: '14px',
                      padding: '1.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.85rem',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                      boxSizing: 'border-box'
                    }}
                  >
                    {/* THUMBNAIL POSTER (HYPERLINKED) */}
                    <div style={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1.5px solid #ecc94b',
                      maxHeight: '220px',
                      backgroundColor: '#070c1b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setIsCatalogueOpen(false);
                      handleReadOnline(pub);
                    }}
                    title="Click to Read Online for Free"
                    >
                      <img
                        src={getCoverImage(pub)}
                        alt={pub.title}
                        style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '220px', objectFit: 'contain' }}
                      />
                    </div>

                    {/* DETAILS */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{
                          fontSize: '0.7rem',
                          fontWeight: '800',
                          color: '#ecc94b',
                          textTransform: 'uppercase'
                        }}>
                          {pub.category}
                        </span>
                        <span style={{
                          fontSize: '0.68rem',
                          fontWeight: '800',
                          padding: '0.15rem 0.45rem',
                          borderRadius: '4px',
                          backgroundColor: '#0284c7',
                          color: 'white'
                        }}>
                          {isUrdu ? 'اردو' : pub.language || 'English'}
                        </span>
                      </div>

                      <h3 
                        onClick={() => {
                          setIsCatalogueOpen(false);
                          handleReadOnline(pub);
                        }}
                        style={{
                          fontSize: '1.05rem',
                          fontWeight: '900',
                          color: 'white',
                          margin: 0,
                          cursor: 'pointer',
                          lineHeight: '1.3',
                          direction: isUrdu ? 'rtl' : 'ltr',
                          fontFamily: isUrdu ? '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", serif' : 'inherit'
                        }}
                      >
                        {pub.title}
                      </h3>

                      <p style={{
                        fontSize: '0.78rem',
                        color: '#94a3b8',
                        margin: 0,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.4'
                      }}>
                        {pub.description}
                      </p>

                      <div style={{ fontSize: '0.75rem', color: '#cbd5e1', marginTop: 'auto', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Pages: <strong>{pub.pageCount}</strong></span>
                        <span style={{ color: '#ecc94b', fontWeight: '800' }}>PKR {pub.price.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button
                        onClick={() => {
                          setIsCatalogueOpen(false);
                          handleReadOnline(pub);
                        }}
                        style={{
                          padding: '0.55rem',
                          borderRadius: '8px',
                          backgroundColor: '#16a34a',
                          color: 'white',
                          border: 'none',
                          fontWeight: '800',
                          fontSize: '0.78rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <BookOpen size={14} />
                        <span>Read Free</span>
                      </button>

                      <button
                        onClick={() => {
                          setIsCatalogueOpen(false);
                          handleDownloadClick(pub);
                        }}
                        style={{
                          padding: '0.55rem',
                          borderRadius: '8px',
                          backgroundColor: purchased ? '#059669' : '#ecc94b',
                          color: purchased ? '#ffffff' : '#0f172a',
                          border: 'none',
                          fontWeight: '900',
                          fontSize: '0.78rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.3rem'
                        }}
                      >
                        <Download size={14} />
                        <span>{purchased ? 'Download' : 'Subscribe'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      )}

      {/* QR CODE MODAL POPUP */}
      {qrModalPub && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100000,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#0f172a',
            border: '2px solid #ecc94b',
            borderRadius: '20px',
            maxWidth: '380px',
            width: '100%',
            padding: '1.75rem',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 20px 50px rgba(0,0,0,0.9)'
          }}>
            <button
              onClick={() => setQrModalPub(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: '#1e293b',
                border: 'none',
                borderRadius: '8px',
                color: '#cbd5e1',
                padding: '0.4rem',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>

            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: 'rgba(236,201,75,0.2)',
              border: '1px solid #ecc94b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem auto'
            }}>
              <QrCode size={24} color="#ecc94b" />
            </div>

            <h3 style={{ fontSize: '1.15rem', fontWeight: '900', color: 'white', margin: '0 0 0.5rem 0' }}>
              {qrModalPub.title}
            </h3>
            
            <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '0 0 1rem 0' }}>
              Scan with any mobile smartphone camera for direct access and mobile reading.
            </p>

            {/* QR CODE DISPLAY */}
            <div style={{
              backgroundColor: 'white',
              padding: '1rem',
              borderRadius: '12px',
              display: 'inline-block',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              marginBottom: '1rem'
            }}>
              <img
                src={qrModalPub.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`https://www.elumlaw.com/downloads?id=${qrModalPub._id}`)}`}
                alt="Publication QR Code"
                style={{ width: '180px', height: '180px', display: 'block' }}
              />
            </div>

            <div style={{
              padding: '0.5rem',
              backgroundColor: '#090d16',
              border: '1px solid #1e293b',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              color: '#94a3b8',
              wordBreak: 'break-all',
              marginBottom: '1rem'
            }}>
              https://www.elumlaw.com/downloads?id={qrModalPub._id}
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(`https://www.elumlaw.com/downloads?id=${qrModalPub._id}`);
                alert('Direct publication link copied to clipboard!');
              }}
              style={{
                width: '100%',
                padding: '0.65rem',
                borderRadius: '10px',
                backgroundColor: '#1e293b',
                color: '#ecc94b',
                border: '1px solid #475569',
                fontWeight: '800',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem'
              }}
            >
              <Share2 size={16} />
              <span>Copy Direct Link</span>
            </button>
          </div>
        </div>
      )}

      {/* FULLSCREEN POSTER & BIBLIOGRAPHIC DETAILS MODAL */}
      {titlePageModalPub && (
        <div 
          onClick={() => setTitlePageModalPub(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            backgroundColor: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0f172a',
              border: '2px solid #ecc94b',
              borderRadius: '20px',
              maxWidth: '850px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '2rem',
              position: 'relative',
              boxShadow: '0 20px 50px rgba(0,0,0,0.95)',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem'
            }}
          >
            <button
              onClick={() => setTitlePageModalPub(null)}
              style={{
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem',
                backgroundColor: '#1e293b',
                border: 'none',
                borderRadius: '8px',
                color: '#cbd5e1',
                padding: '0.45rem',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BookOpen size={22} color="#ecc94b" />
              <h3 style={{ fontSize: '1.3rem', fontWeight: '900', color: 'white', margin: 0 }}>
                Authentic Book Poster &amp; Frontispiece
              </h3>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
              {/* HIGH RESOLUTION COVER PREVIEW */}
              <div style={{
                width: '320px',
                flexShrink: 0,
                borderRadius: '14px',
                border: '2px solid #ecc94b',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                backgroundColor: '#070c1b'
              }}>
                <img
                  src={getCoverImage(titlePageModalPub)}
                  alt={titlePageModalPub.title}
                  style={{ width: '100%', display: 'block', height: 'auto' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = getCoverImage(titlePageModalPub);
                  }}
                />
              </div>

              {/* BIBLIOGRAPHIC DETAILS */}
              <div style={{ flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#ecc94b', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {titlePageModalPub.category}
                  </span>
                  <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: 'white', margin: '0.2rem 0 0.25rem 0', lineHeight: '1.2' }}>
                    {titlePageModalPub.title}
                  </h2>
                  {titlePageModalPub.subtitle && (
                    <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#cbd5e1', margin: 0 }}>
                      {titlePageModalPub.subtitle}
                    </p>
                  )}
                </div>

                <div style={{ backgroundColor: '#1e293b', padding: '0.85rem 1rem', borderRadius: '10px', border: '1px solid #334155' }}>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '800', marginBottom: '4px' }}>
                    Authors &amp; Contributors
                  </span>
                  {titlePageModalPub.authors.map((auth, aIdx) => (
                    <div key={aIdx} style={{ fontSize: '0.85rem', color: '#f8fafc', fontWeight: '600', marginTop: '2px' }}>
                      • {auth}
                    </div>
                  ))}
                </div>

                <div style={{ backgroundColor: '#1e293b', padding: '0.85rem 1rem', borderRadius: '10px', border: '1px solid #334155' }}>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '800', marginBottom: '4px' }}>
                    Cataloging in Publication Data
                  </span>
                  <div style={{ fontSize: '0.8rem', color: '#cbd5e1', lineHeight: '1.5' }}>
                    Publisher: <strong>Elum Law Academic &amp; Research Press</strong><br />
                    Jurisdiction: <strong>Islamic Republic of Pakistan</strong><br />
                    ISBN: <strong>978-969-8924-21-0</strong><br />
                    Format: <strong>Full-text PDF &amp; Structured JSON Compendium</strong><br />
                    Total Pages: <strong>{titlePageModalPub.pageCount} Pages</strong>
                  </div>
                </div>

                {/* MODAL ACTION BUTTONS */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setTitlePageModalPub(null);
                      handleDownloadClick(titlePageModalPub);
                    }}
                    style={{
                      flex: '1 1 180px',
                      padding: '0.75rem 1.25rem',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: isPurchased(titlePageModalPub) ? '#16a34a' : '#ecc94b',
                      color: isPurchased(titlePageModalPub) ? 'white' : '#0f172a',
                      fontWeight: '900',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    <Download size={16} />
                    <span>{isPurchased(titlePageModalPub) ? 'Download Complete PDF' : `Subscribe & Download (PKR ${titlePageModalPub.price.toLocaleString()})`}</span>
                  </button>

                  <button
                    onClick={() => {
                      setTitlePageModalPub(null);
                      handleReadOnline(titlePageModalPub);
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '10px',
                      border: '1px solid #6366f1',
                      backgroundColor: '#4f46e5',
                      color: 'white',
                      fontWeight: '800',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem'
                    }}
                  >
                    <BookOpen size={16} />
                    <span>Read Online</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT MODAL FOR SUBSCRIPTION */}
      {paymentPub && (
        <PaymentModal
          paymentData={{
            id: paymentPub._id,
            amount: paymentPub.price,
            type: 'publication_subscription'
          }}
          onClose={() => setPaymentPub(null)}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* FULL-SCREEN DRM SECURE READER */}
      {readerPub && (
        <SecurePublicationReader
          publication={readerPub}
          userEmail="reader@elumlaw.com"
          userName="Valued Legal Scholar"
          onClose={() => setReaderPub(null)}
        />
      )}

    </div>
  );
};

export default DownloadsPage;
